import * as React from "react";
import { useEffect, useState } from "react";
import { Modal, Button, Input, Select, DatePicker, Form, Row, Col } from "antd";
import dayjs from "dayjs";

import { useTexts } from "../../hooks/useTexts";
import PhoneInput from "../PhoneInput";
import Place from "../../types/Place";
import { fetchPlaces } from "../../api/places";
import { convertStringToDBFormat, parseDate } from "../../util/date";
import { editMediator } from "../../api/mediator";

import Mediator from "../../types/Mediator"
import EditMediatorRequest from "../../types/EditMediatorRequest";
import { convertPhoneNumber } from "../../util/common";
import { phonePrefix } from "../../data/country";

const EditMediatorModal = ({ open, state, setState, successCallback, closeModal }: {
    open: boolean,
    state: Mediator,
    setState: (data: Mediator) => void,
    successCallback: () => void,
    closeModal: () => void
}) => {
    const { t } = useTexts();
    const [places, setPlaces] = useState<Place[]>([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setState({ ...state, [e.target.name]: e.target.value });
    };

    const handleSubmit = (): void => {
        setLoading(true);
        const request: EditMediatorRequest = {
            firstname: state.firstname,
            lastname: state.lastname,
            phoneNumber: convertPhoneNumber(state.phoneNumber)?.toString(),
            birthDate: convertStringToDBFormat(state.birthDate),
            placeId: state.place?.id
        };
        editMediator(state.id, request)
            .then(() => {
                successCallback();
            })
            .catch(error => console.error("ERROR", error))
            .finally(() => setLoading(false));
    };

    const getPlaces = () => {
        fetchPlaces().then(response => {
            setPlaces(response?.content || []);
        }).catch(error => console.error("ERROR", error));
    };

    const parsePhoneNumber = (phoneNumber: string) => {
        const parts = phoneNumber?.split(phonePrefix);
        return parts[parts.length - 1];
    };

    useEffect(() => {
        getPlaces();
    }, []);

    // Form value sync to local state
    const handleValuesChange = (changedValues: any, allValues: any) => {
        if (changedValues.birthDate) {
            const parsedDate = parseDate(changedValues.birthDate);
            setState({ ...state, birthDate: !!parsedDate ? parsedDate : '' });
        } else if (changedValues.place) {
            setState({
                ...state, place: {
                    ...state.place,
                    id: Number(changedValues.place)
                }
            });
        }
    };

    // Update form when state changes from outside
    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                firstname: state.firstname,
                lastname: state.lastname,
                birthDate: dayjs(state.birthDate.toString()),
                place: state.place?.id
            });
        }
    }, [open, state, form]);

    return (
        <Modal
            title={t('mediator', 'editMediator')}
            open={open}
            onCancel={closeModal}
            width={768}
            footer={null}
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('user', 'firstname')} required>
                            <Input
                                name="firstname"
                                value={state.firstname}
                                onChange={handleInput}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('user', 'lastname')} required>
                            <Input
                                name="lastname"
                                value={state.lastname}
                                onChange={handleInput}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16} align="middle">
                    <Col xs={24} md={12}>
                        <Form.Item label={t('mediator', 'phone')} required>
                            <PhoneInput
                                error={!state.phoneNumber}
                                value={parsePhoneNumber(state.phoneNumber.toString())}
                                onChange={value => setState({ ...state, phoneNumber: value })}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={t('mediator', 'birthDate')} name="birthDate" required>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label={t('mediator', 'placeObject')} name="place" required>
                            <Select
                                placeholder={t('mediator', 'placeObject')}
                                allowClear
                                showSearch
                                options={places.map((option: Place) => ({
                                    label: `${option.name} (${option.city})`,
                                    value: option.id
                                }))}
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                    <Button onClick={closeModal}>
                        {t('common', 'close')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        disabled={!state.firstname || !state.lastname || !state.phoneNumber || !state.birthDate || !state.place}
                    >
                        {t('mediator', 'editMediator')}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

export default EditMediatorModal;